// Chapter-bundle output schemas — Session 19BF.
//
// Authoritative LLM output shapes for the **认识论改造** of Phase 3:
//   chapter-objective : single LLM call produces mcq + tf + flashcard for
//                        ONE chapter (cohesive "facts/discrimination" thinking)
//   chapter-subjective: single LLM call produces essay + free_response +
//                        proof_drill for ONE chapter (cohesive "argumentation"
//                        thinking)
//   chapter-reference : single LLM call produces glossary + lecture_notes +
//                        mindmap for ONE chapter (cohesive "knowledge index"
//                        thinking)
//   cross-chapter     : single LLM call produces 30%-quota cross-chapter
//                        comparison items (mcq/tf/essay/free_response with
//                        section='cross_chapter') across ALL chapters
//
// Why bundles vs separate stages (per Session 19BF design):
//   - Same chapter context, one LLM thinking pass → glossary terms align with
//     mcq/essay terminology; lecture_notes covers what mcq/essay actually
//     focus on; no duplicate facts across mcq/tf/flashcard
//   - 60% fewer LLM calls (10 stage × 7 ch + qa = 71  →  3 group × 7 ch + cross
//     + heal + mock = ~24)
//   - 70% cost reduction (single prompt overhead amortized across 3-4 item types)
//
// Item-level schemas UNCHANGED — these bundle schemas only wrap existing
// McqSchema, FlashcardSchema, etc. Downstream lint / repair / UI continues
// to work on item arrays after stage destructures the bundle.
//
// 70/30 quota policy:
//   chapter-objective.mcqs       = 70% of chapter mcq target  (cross handles 30%)
//   chapter-objective.trueFalse  = 70% of chapter tf target
//   chapter-objective.flashcards = 100%  (chapter-bound by nature — concept cards)
//   chapter-subjective.essays    = 70%
//   chapter-subjective.freeResponses = 70%
//   chapter-subjective.proofDrills   = 70%   (陛下 19BF: 逻辑学 modal→possible-world→K3
//                                               存在跨 chapter 推导，proof_drill 也需 30%
//                                               保留给 cross-chapter integration)
//   chapter-reference.*          = 100% (glossary 在 audit-heal 中 dedup-merge 跨 chapter)
//
// masteryLevel + contentLanguage:
//   These two come from CourseDraft and influence EVERY bundle's prompt.
//   masteryLevel ∈ {novice, partial, reviewed} → controls depth/density
//                                                  (novice → very explicit
//                                                  step-by-step; reviewed →
//                                                  concise outline / cheat-sheet)
//   contentLanguage ∈ {en, en_zh_gloss, zh_en_terms, zh} → output language mode
//   Both threaded via buildSystemPrompt (already handles contentLanguage;
//   MASTERY_TONE added by 19BF in prompts/domain/mastery-tone.ts).
//
// Anti-laziness: each bundle includes a __counts self-report field. LLM must
// declare counts; orchestrator validates against bundleTarget before accepting.
// 19BH Day 3 Step 3.5: subjective bundle (essay/freeResp/proofDrill), cross-
// chapter bundles, and audit-heal output schemas deleted. Only the two
// minimal cohesion bundles (objective + reference) remain — see
// packages/ingest/src/generate/bundle-runner.ts header for the v4 rationale.
import { z } from 'zod';
import { McqSchema } from './mcq.js';
import { TrueFalseSchema } from './true-false.js';
import { FlashcardSchema } from './flashcard.js';
import { GlossaryItemSchema } from './glossary.js';
import { LectureNoteSchema } from './lecture-note.js';
import { MindmapSchema } from './mindmap.js';
/**
 * 19BH+2 Day 1 Round 3 · cs3052 Run 3 raw output showed LLM emitting
 * `shortfallReason: null` instead of omitting the field — schema's `.optional()`
 * accepts undefined but not null. Coerce null → undefined so LLM's literal-null
 * habit doesn't fail the bundle. Also drops empty-string shortfallReason
 * (LLM emit `""` violates min(1) but means "no shortfall" semantically).
 */
function coerceBundleShortfall(obj) {
    if (typeof obj !== 'object' || obj === null)
        return obj;
    const o = obj;
    if (o['shortfallReason'] === null || o['shortfallReason'] === '') {
        const { shortfallReason: _omit, ...rest } = o;
        return rest;
    }
    return obj;
}
export function chapterObjectiveBundleSchema(opts) {
    return z.preprocess(coerceBundleShortfall, z.object({
        /**
         * 19BG+2 Phase 4 (DEBT-19BH-SHARED-CONCEPTS-SCHEMA): the chapter's 5-8
         * canonical concepts. Every item below should set conceptTag = one of these.
         * Drives dispersion metric (jaccard) — the bigger the chapter's
         * sharedConcepts vocabulary AND the more even item-coverage across them,
         * the higher dispersion. Optional for back-compat with pre-19BG+2 ingest;
         * audit-heal back-fills when missing.
         */
        sharedConcepts: z.array(z.string().min(1).max(80)).min(5).max(8).nullish(),
        mcqs: z.array(McqSchema),
        trueFalse: z.array(TrueFalseSchema),
        flashcards: z.array(FlashcardSchema),
        /** LLM self-reports counts. Orchestrator validates against target.
         *  When counts don't match items.length, schema retry hint surfaces both. */
        __counts: z.object({
            mcqs: z.number().int().nonnegative(),
            trueFalse: z.number().int().nonnegative(),
            flashcards: z.number().int().nonnegative(),
        }).optional(),
        /** When LLM cannot meet the target due to thin source material, declare
         *  here — orchestrator logs but doesn't retry-spam.
         *  ANTI-LAZINESS (19BI Bug A): shortfallReason is an escape hatch for
         *  GENUINELY thin source where ALL types struggle. It is NOT a license to
         *  skip individual harder types while producing others fine. The refine
         *  below rejects "partial zero" — where some types are 0 but others meet
         *  their floor — because source material cannot be "thin for MCQs but
         *  rich enough for 10 TF statements and 12 flashcards" simultaneously. */
        shortfallReason: z.string().min(1).max(800).optional(),
    }).refine((b) => {
        const mcqOk = b.mcqs.length >= opts.mcqMin;
        const tfOk = b.trueFalse.length >= opts.tfMin;
        const fcOk = b.flashcards.length >= opts.fcMin;
        const allOk = mcqOk && tfOk && fcOk;
        // All types meet floor → always pass
        if (allOk)
            return true;
        // No shortfallReason → fail
        if (b.shortfallReason === undefined)
            return false;
        // Has shortfallReason: distinguish genuine thin source from LLM laziness.
        // ALL three zero → genuinely thin source, allow.
        const allZero = b.mcqs.length === 0 && b.trueFalse.length === 0 && b.flashcards.length === 0;
        if (allZero)
            return true;
        // PARTIAL zero (some types 0, others ≥floor) → LLM laziness, reject.
        // Source material cannot be thin for MCQs but rich for TF+FC simultaneously.
        return false;
    }, { message: `objective bundle: partial-zero detected (some types 0 but others meet floor) — shortfallReason cannot excuse skipping individual types. Produce ALL types or explain why ALL are thin (need mcqs≥${opts.mcqMin}, trueFalse≥${opts.tfMin}, flashcards≥${opts.fcMin})` }));
}
/** Back-compat default alias matching Day 2B inline floor (cs3052 baseline ·
 *  70% chapter-share for mcq/tf, 100% for fc). New callers should use
 *  chapterObjectiveBundleSchema({...}) with manifest-driven floor. */
export const ChapterObjectiveBundleSchema = chapterObjectiveBundleSchema({ mcqMin: 8, tfMin: 5, fcMin: 8 });
export function chapterReferenceBundleSchema(opts) {
    return z.preprocess(coerceBundleShortfall, z.object({
        /** 19BG+2 Phase 4 sharedConcepts — see chapterObjectiveBundleSchema doc.
         *  Reference bundle's sharedConcepts SHOULD agree with objective/subjective
         *  bundles for the same chapter (the canonical 5-8 concept list per chapter
         *  is invariant across the 3 bundles). */
        sharedConcepts: z.array(z.string().min(1).max(80)).min(5).max(8).nullish(),
        glossaryCandidates: z.array(GlossaryItemSchema),
        lectureNotes: z.array(LectureNoteSchema),
        mindmap: MindmapSchema.optional(),
        __counts: z.object({
            glossaryCandidates: z.number().int().nonnegative(),
            lectureNotes: z.number().int().nonnegative(),
            mindmap: z.number().int().min(0).max(1).optional(),
        }).optional(),
        shortfallReason: z.string().min(1).max(800).optional(),
    }).refine((b) => {
        const glossOk = b.glossaryCandidates.length >= opts.glossMin;
        const lectureOk = b.lectureNotes.length >= opts.lectureMin;
        const allOk = glossOk && lectureOk;
        if (allOk)
            return true;
        if (b.shortfallReason === undefined)
            return false;
        // Both zero → genuinely thin source
        const allZero = b.glossaryCandidates.length === 0 && b.lectureNotes.length === 0;
        if (allZero)
            return true;
        // Partial zero → LLM laziness, reject
        return false;
    }, { message: `reference bundle: partial-zero detected (some types 0 but others meet floor) — shortfallReason cannot excuse skipping individual types. Produce ALL types or explain why ALL are thin (need glossaryCandidates≥${opts.glossMin}, lectureNotes≥${opts.lectureMin})` }));
}
/** Back-compat default alias matching Day 2B inline floor (glossaryCandidates=6 · lectureNotes=1). */
export const ChapterReferenceBundleSchema = chapterReferenceBundleSchema({ glossMin: 6, lectureMin: 1 });
//# sourceMappingURL=chapter-bundle.js.map